'use client';

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import type { SessionItem } from '@/data/sessions';

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

type Session = SessionItem;

type SupabaseSessionRow = {
  id: number;
  formation: string;
  formationSlug?: string;
  date: string;
  lieu: string;
  places: number;
  places_restantes: number;
};

interface PlanningClientProps {
  initialSessions: Session[];
}

export default function PlanningClient({ initialSessions }: PlanningClientProps) {
  const [allSessions, setAllSessions] = useState<Session[]>(initialSessions);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLieu, setSelectedLieu] = useState('Tous');
  const [searchTerm, setSearchTerm] = useState('');

  const availableLieux = useMemo(
    () => ['Tous', ...Array.from(new Set(allSessions.map((session) => session.lieu)))],
    [allSessions],
  );

  const filteredSessions = useMemo(() => {
    return allSessions.filter((session) => {
      const matchesLieu = selectedLieu === 'Tous' || session.lieu === selectedLieu;
      const term = searchTerm.trim().toLowerCase();

      const matchesSearch =
        term.length === 0 ||
        session.formation.toLowerCase().includes(term) ||
        session.lieu.toLowerCase().includes(term);

      return matchesLieu && matchesSearch;
    });
  }, [allSessions, searchTerm, selectedLieu]);

  useEffect(() => {
    const hasSupabaseConfig =
      Boolean(process.env.NEXT_PUBLIC_SUPABASE_URL) && Boolean(process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY);

    if (!hasSupabaseConfig) {
      setIsLoading(false);
      return;
    }

    const loadSupabase = async () => {
      try {
        const { createClient } = await import('@supabase/supabase-js');

        if (!process.env.NEXT_PUBLIC_SUPABASE_URL || !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY) {
          throw new Error('Variables d\'environnement Supabase manquantes');
        }

        const supabase = createClient(
          process.env.NEXT_PUBLIC_SUPABASE_URL,
          process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
        );

        return supabase;
      } catch (error) {
        console.error('Erreur lors du chargement de Supabase:', error);
        throw error;
      }
    };

    const fetchSessions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const supabase = await loadSupabase();

        const { data, error: supabaseError } = await supabase
          .from('sessions')
          .select('id, formation, formationSlug, date, lieu, places, places_restantes')
          .order('date', { ascending: true });

        if (supabaseError) {
          throw supabaseError;
        }

        if (Array.isArray(data) && data.length > 0) {
          const normalizedSessions: Session[] = (data as SupabaseSessionRow[]).map((row) => ({
            id: row.id,
            formation: row.formation,
            formationSlug: row.formationSlug ?? '',
            date: row.date,
            lieu: row.lieu,
            places: row.places,
            places_restantes: row.places_restantes,
          }));

          setAllSessions(normalizedSessions);
        }
      } catch (err: unknown) {
        console.error('Erreur lors de la récupération des sessions:', err);
        const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue lors de la récupération des sessions.';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();
  }, []);

  return (
    <div className="container mx-auto px-4 py-10 lg:py-12">
      <section className="mb-8 rounded-3xl border border-white/10 bg-gradient-to-br from-[#06122F] via-[#050B18] to-[#0A1638] p-6 shadow-[0_20px_80px_-50px_rgba(0,0,0,0.8)] md:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <h1 className="mb-3 text-3xl font-bold text-white md:text-5xl">Planning des formations</h1>
            <p className="text-base text-white/75 md:text-lg">
              Consultez les sessions à venir, filtrez par lieu, puis contactez-nous pour reserver votre place.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button asChild variant="cta-outline">
              <Link href="/formations">Voir le catalogue</Link>
            </Button>
            <Button asChild variant="cta">
              <Link href="/contact">Demander une inscription</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="mb-8 rounded-2xl border border-white/10 bg-white/5 p-4 shadow-sm backdrop-blur md:p-6">
        <div className="grid gap-4 md:grid-cols-[1fr_260px]">
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Rechercher une formation ou un lieu"
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-white placeholder:text-white/40 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/20"
          />

          <select
            value={selectedLieu}
            onChange={(event) => setSelectedLieu(event.target.value)}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-white focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/20"
            aria-label="Filtrer les sessions par lieu"
          >
            {availableLieux.map((lieu) => (
              <option key={lieu} value={lieu}>
                {lieu === 'Tous' ? 'Tous les lieux' : lieu}
              </option>
            ))}
          </select>
        </div>
      </section>

      {isLoading ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
        </div>
      ) : (
        <section className="rounded-2xl border border-white/10 bg-white/5 shadow-sm backdrop-blur">
          <div className="border-b border-white/10 px-5 py-4">
            <h2 className="text-lg font-semibold text-white">Sessions disponibles ({filteredSessions.length})</h2>
            {error ? <p className="mt-1 text-sm text-amber-300">Source live indisponible, affichage du planning de reference.</p> : null}
          </div>

          <div className="divide-y divide-white/10">
            {filteredSessions.length > 0 ? (
              filteredSessions.map((session) => (
                <article key={session.id} className="px-5 py-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-white">{session.formation}</h3>
                      <p className="text-sm text-white/70">
                        {dateFormatter.format(new Date(session.date))} • {session.lieu}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {session.formationSlug ? (
                        <Button
                          asChild
                          size="sm"
                          variant="cta-outline"
                          className="border-white/30"
                        >
                          <Link href={`/formations/${session.formationSlug}`}>Voir la formation</Link>
                        </Button>
                      ) : null}
                      <Button
                        asChild
                        size="sm"
                        variant="cta"
                      >
                        <Link href="/contact">S&apos;inscrire</Link>
                      </Button>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div className="px-5 py-12 text-center">
                <p className="text-white/70">Aucune session ne correspond a vos filtres actuels.</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedLieu('Tous');
                  }}
                  className="mt-3 text-sm font-medium text-white/80 hover:text-accent"
                >
                  Reinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="mt-8 rounded-3xl border border-accent/20 bg-white/5 p-6 shadow-sm backdrop-blur">
        <h2 className="text-xl font-semibold text-white">Besoin d&apos;un planning sur mesure ?</h2>
        <p className="mt-2 text-white/75">
          Nous pouvons organiser des sessions intra-entreprise partout en France selon vos contraintes operationnelles.
        </p>
        <div className="mt-4">
          <Button asChild variant="cta">
            <Link href="/contact">Parler a un conseiller</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
